import { Fragment, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Alert, Badge, Box, Button, Heading, SelectInputNative, Spinner, Table, TextInput, TextareaInput,
} from '@hyphen/hyphen-components';

import { api, accessReviewDocumentURL } from '../lib/api.js';
import { Markdown } from './markdown.jsx';
import { useResource } from '../lib/session.js';

/**
 * The list of access reviews, and a form to open one.
 *
 * A review is opened deliberately rather than appearing on a schedule: HAI-019
 * is an annual control someone performs, and the record should say who decided
 * to perform it and when.
 */
export function AccessReviews({ canEdit }) {
  const { data: reviews, error, setData } = useResource('/access-reviews');
  const [period, setPeriod] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);

  async function open(event) {
    event.preventDefault();
    setBusy(true);
    setFailed(null);
    try {
      await api.post('/access-reviews', { period, hai_id: 'HAI-019' });
      setPeriod('');
      setData(await api.get('/access-reviews'));
    } catch (err) {
      setFailed(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !reviews) return <Alert variant="danger">{error}</Alert>;
  if (!reviews) return <Spinner />;

  return (
    <Box gap="3xl">
      <Heading as="h1" size="lg">Access reviews</Heading>
      <Box as="p" color="secondary" maxWidth="48rem">
        Annually, logical access privileges are reviewed by management to ensure access is assigned
        commensurate with job roles (HAI-019). A review draws its population from the latest
        captures, flags what the evidence shows is worth attention, and is signed off once.
      </Box>

      {failed && <Alert variant="warning">{failed}</Alert>}

      {canEdit && (
        <Box as="form" direction="row" gap="sm" alignItems="flex-end" onSubmit={open}>
          <TextInput
            id="period"
            label="Period"
            placeholder="2026 annual"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
          />
          <Button type="submit" variant="primary" isDisabled={busy || !period.trim()} isLoading={busy}>
            Open a review
          </Button>
        </Box>
      )}

      {reviews.length === 0 && <Box color="secondary">No access review has been performed yet.</Box>}

      {reviews.length > 0 && (
        <Table
          rowKey="id"
          isScrollable={{ x: true }}
          columns={[
            {
              heading: 'Period',
              dataKey: 'period',
              render: (_cell, row) => <Link to={`/admin/access-reviews/${row.id}`}>{row.period}</Link>,
            },
            { heading: 'Control', dataKey: 'hai_id' },
            {
              heading: 'Status',
              dataKey: 'status',
              render: (_cell, row) => <StatusBadge review={row} />,
            },
            { heading: 'Accounts', dataKey: 'total' },
            {
              heading: 'Flagged',
              dataKey: 'flagged',
              render: (_cell, row) =>
                row.outstanding > 0
                  ? `${row.flagged} (${row.outstanding} to address)`
                  : row.flagged || '—',
            },
          ]}
          rows={reviews}
        />
      )}
    </Box>
  );
}

/**
 * How a review stands. Three states rather than two: open, closed with a
 * signature, and closed without one — because the last is not the same as
 * either of the others and reading it as "closed" would hide that nothing was
 * attested to.
 */
function StatusBadge({ review }) {
  if (review.status === 'open') return <Badge variant="soft" color="warning">open</Badge>;
  if (review.signed) return <Badge variant="soft" color="green">signed off</Badge>;
  return <Badge variant="soft" color="grey">closed, not signed</Badge>;
}

/**
 * One review.
 *
 * One list, not two. A separate findings section meant every flagged account
 * appeared twice, and neither copy sat beside the accounts it should be compared
 * with — an owner is only interesting next to the other owners. So the findings
 * are in the rows they belong to, sorted to the top of their system, and the
 * page says how many still want a decision.
 *
 * The document keeps its summary of what was flagged. A reader handed the file
 * wants that first; a reviewer working through the population does not.
 */
export function AccessReview({ canEdit }) {
  const { reviewID } = useParams();
  const navigate = useNavigate();
  const path = `/access-reviews/${encodeURIComponent(reviewID)}`;
  const { data: review, error, setData, setError } = useResource(path);
  const [busy, setBusy] = useState(null);

  async function act(name, run) {
    setBusy(name);
    setError(null);
    try {
      setData(await run());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  }

  const reload = async () => setData(await api.get(path));

  if (error && !review) return <Alert variant="danger">{error}</Alert>;
  if (!review) return <Spinner />;

  const open = review.status === 'open';
  const signedOff = review.signed;
  const editable = canEdit && open;

  return (
    <Box gap="4xl">
      <Box gap="lg">
        <Box direction="row" alignItems="center" gap="xl">
          <Heading as="h1" size="lg">{review.period}</Heading>
          <StatusBadge review={review} />
        </Box>

        {error && <Alert variant="warning">{error}</Alert>}

        <Box color="secondary" fontSize="sm">
          {open && `Started by ${review.opened_by}.`}
          {!open && signedOff && `Signed off by ${review.signed_off_by}. Nothing below can be changed.`}
          {!open && !signedOff &&
            `Closed by ${review.closed_by} without being signed off: ${review.closed_reason}`}
        </Box>

        {review.total > 0 && signedOff && (
          <Alert variant="success">
            {`${review.signed_off_by} reviewed all ${review.total} accounts and confirmed access is appropriate to each holder's role, except where noted.`}
          </Alert>
        )}
        {review.total > 0 && open && (
          <Alert variant="info">
            {`Signing off attests that all ${review.total} accounts were reviewed and that access is appropriate to each holder's role, except where noted.`}
          </Alert>
        )}
        {!open && !signedOff && (
          <Alert variant="warning">
            This review was closed without being signed off, so it does not evidence the control.
          </Alert>
        )}

        {editable && (
          <Box direction="row" gap="sm" alignItems="center">
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy === 'populate'}
              isDisabled={Boolean(busy)}
              onClick={() => act('populate', () => api.post(`${path}/populate`, {}))}
            >
              {review.total === 0 ? 'Collect and draw the population' : 'Collect again and refresh'}
            </Button>
          </Box>
        )}

        {review.total > 0 && (
          <Box direction="row" gap="sm" alignItems="center">
            {/* A link rather than a button: this navigates to a file. */}
            <a className="export-link" href={accessReviewDocumentURL(review.id)}>
              Download as Markdown
            </a>
            <Box as="span" fontSize="sm" color="secondary">
              The same review as a file, for handing to the auditor.
            </Box>
          </Box>
        )}
      </Box>

      {review.total === 0 && (
        <Box color="secondary">
          Nothing drawn yet. The population comes from the latest Workspace, GCP and GitHub captures.
        </Box>
      )}

      {review.principals.length > 0 && (
        <Population principals={review.principals} editable={editable} onSaved={reload} />
      )}

      {review.total > 0 && <DocumentPreview document={review.document} />}

      {open && review.total > 0 && (
        <SignOff
          review={review}
          canEdit={canEdit}
          busy={busy === 'sign-off'}
          onSignOff={() => act('sign-off', () => api.post(`${path}/sign-off`, {}))}
        />
      )}

      {canEdit && open && (
        <EndWithoutSigning
          path={path}
          onClosed={reload}
          onDeleted={() => navigate('/admin/access-reviews')}
        />
      )}
    </Box>
  );
}

/**
 * The file the auditor is handed, shown as what it is.
 *
 * The page above renders the review; this is a preview of the export, so it is
 * monospaced and labelled as a preview rather than dressed up as the document.
 */
function DocumentPreview({ document }) {
  const [shown, setShown] = useState(false);
  return (
    <Box gap="lg">
      <Box direction="row" gap="sm" alignItems="center">
        <Heading as="h2" size="md">Document preview</Heading>
        <Button size="sm" variant="secondary" onClick={() => setShown(!shown)}>
          {shown ? 'Hide' : 'Show'}
        </Button>
      </Box>
      <Box color="secondary" fontSize="sm">
        What the download contains, as it will be handed over.
      </Box>
      {shown && (
        <Box padding="xl" background="primary" radius="md" borderWidth="sm" borderColor="subtle">
          <Markdown>{document}</Markdown>
        </Box>
      )}
    </Box>
  );
}

/**
 * Signing off, last on the page because it is the last thing done.
 *
 * Management performs this control and management here is one person, so the
 * page says whose signature it is waiting for rather than silently disabling a
 * button for everyone else.
 */
function SignOff({ review, canEdit, busy, onSignOff }) {
  const waitingOnSomeoneElse = canEdit && !review.can_sign_off && review.outstanding === 0;
  return (
    <Box gap="lg" padding="xl" background="secondary" radius="md">
      <Heading as="h2" size="md">Sign off</Heading>
      <Box as="p" maxWidth="48rem">
        Signing off attests that all {review.total} accounts above were reviewed and that access is
        appropriate to each holder's role, except where the findings note otherwise.
      </Box>
      {review.outstanding > 0 && (
        <Box as="span" fontSize="sm" color="secondary">
          {review.outstanding} finding{review.outstanding === 1 ? '' : 's'} still to address.
        </Box>
      )}
      {waitingOnSomeoneElse && review.signer && (
        <Box as="span" fontSize="sm" color="secondary">
          {review.signer} signs this review off.
        </Box>
      )}
      <Box direction="row">
        <Button
          variant="primary"
          isLoading={busy}
          isDisabled={!review.can_sign_off || busy}
          onClick={onSignOff}
        >
          Sign off
        </Button>
      </Box>
    </Box>
  );
}

/**
 * Who holds an account, answered where the question is asked.
 *
 * Offered only on a flagged account nobody is recorded as holding — which is
 * the flag it answers. A service account is not a person and is never asked
 * about.
 */
function Identify({ principal, onSaved }) {
  const [person, setPerson] = useState('');
  const [affiliation, setAffiliation] = useState('staff');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(null);
  const [deciding, setDeciding] = useState(false);

  async function save() {
    setSaving(true);
    setFailed(null);
    try {
      await api.patch(`/access-review-principals/${principal.id}`, { person, affiliation });
      await onSaved();
    } catch (err) {
      setFailed(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (deciding) return <Note principal={principal} onSaved={onSaved} />;

  return (
    <Box gap="sm">
      {failed && <Alert variant="warning">{failed}</Alert>}
      {/*
        Buttons under the fields rather than beside them. Two inputs in a row
        take the width and leave a button to wrap its label onto three lines,
        which is what this did.
      */}
      <Box direction="row" gap="sm" alignItems="flex-end">
        <TextInput
          id={`person-${principal.id}`}
          label="Who holds this?"
          placeholder="name@hyphen.ai"
          value={person}
          onChange={(event) => setPerson(event.target.value)}
        />
        <SelectInputNative
          id={`affiliation-${principal.id}`}
          label="They are"
          value={affiliation}
          options={[
            { value: 'staff', label: 'Staff' },
            { value: 'contractor', label: 'A contractor' },
          ]}
          onChange={(event) => setAffiliation(event.target.value)}
        />
      </Box>
      <Box direction="row" gap="sm" alignItems="center">
        <Button size="sm" isLoading={saving} isDisabled={!person.trim()} onClick={save}>
          Record
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setDeciding(true)}>
          Write it up instead
        </Button>
      </Box>
      <Box as="span" fontSize="sm" color="secondary">
        Recorded once and remembered for every future review.
      </Box>
    </Box>
  );
}

/**
 * What is wrong with a flagged account, and where it is being dealt with.
 *
 * A sentence and an optional link, because that is what flagging something is.
 * An earlier cut asked for a disposition — keep, remove, downgrade, exception —
 * and a justification beside it, which is the same sentence in two pieces, one
 * of them a vocabulary the reviewer has to learn first.
 *
 * Save and Cancel rather than saving as you type: this is the record an auditor
 * reads, and it should be written deliberately.
 */
function Note({ principal, onSaved, onCancel }) {
  const [note, setNote] = useState(principal.note ?? '');
  const [url, setURL] = useState(principal.remediation_url ?? '');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(null);

  const changed =
    note !== (principal.note ?? '') || url !== (principal.remediation_url ?? '');

  function reset() {
    setNote(principal.note ?? '');
    setURL(principal.remediation_url ?? '');
    setFailed(null);
    if (onCancel) onCancel();
  }

  async function write(body) {
    setSaving(true);
    setFailed(null);
    try {
      await api.patch(`/access-review-principals/${principal.id}`, body);
      await onSaved();
    } catch (err) {
      setFailed(err.message);
    } finally {
      setSaving(false);
    }
  }

  const save = () => write({ note, remediation_url: url });

  return (
    <Box gap="sm">
      {failed && <Alert variant="warning">{failed}</Alert>}
      <TextareaInput
        id={`note-${principal.id}`}
        label={principal.finding ? 'What was decided about it' : "What's wrong with it"}
        rows={2}
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      <TextInput
        id={`url-${principal.id}`}
        label="Tracked at (optional)"
        placeholder="https://github.com/Hyphen/trust/issues/…"
        value={url}
        onChange={(event) => setURL(event.target.value)}
      />
      <Box direction="row" gap="sm" alignItems="center">
        <Button size="sm" isLoading={saving} isDisabled={!changed} onClick={save}>Save</Button>
        {onCancel && (
          <Button size="sm" variant="secondary" isDisabled={saving} onClick={reset}>Cancel</Button>
        )}
        {changed && <Box as="span" fontSize="sm" color="secondary">Unsaved changes</Box>}
      </Box>
    </Box>
  );
}

/**
 * Taking back a flag a person raised.
 *
 * In the row rather than in the form, because it undoes the row's state rather
 * than editing its contents — and because a button that empties the fields you
 * are looking at reads as a mistake when it sits among them.
 */
function Unflag({ principal, onSaved }) {
  const [saving, setSaving] = useState(false);

  async function unflag() {
    setSaving(true);
    try {
      await api.patch(`/access-review-principals/${principal.id}`, {
        note: '',
        remediation_url: '',
      });
      await onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Button size="sm" variant="secondary" isLoading={saving} onClick={unflag}>
      Unflag
    </Button>
  );
}

/**
 * The reviewed population: every account, flagged first within its system, with
 * the findings in the rows they belong to.
 */
function Population({ principals, editable, onSaved }) {
  const [editing, setEditing] = useState(null);

  const systems = [];
  const bySystem = {};
  for (const principal of principals) {
    if (!bySystem[principal.system]) {
      bySystem[principal.system] = [];
      systems.push(principal.system);
    }
    bySystem[principal.system].push(principal);
  }

  const outstanding = principals.filter((p) => p.finding && !p.note).length;
  const flagged = principals.filter((p) => p.flagged).length;

  // Flagged first within each group: the rows that want something are the rows
  // worth meeting first, and sorting beats a separate list because they stay
  // beside the accounts they should be compared with.
  const order = (rows) =>
    [...rows].sort((a, b) => Number(Boolean(b.flagged)) - Number(Boolean(a.flagged)));

  return (
    <Box gap="xl">
      <Heading as="h2" size="md">Review ({principals.length})</Heading>
      <Box color="secondary" fontSize="sm">
        {outstanding > 0
          ? `${outstanding} of ${principals.length} need a decision. Everything else was reviewed and is accepted by signing off.`
          : flagged > 0
            ? `${flagged} flagged, all addressed. Signing off accepts the rest as reviewed.`
            : 'Nothing was flagged. Signing off is the statement that all of these were reviewed.'}
      </Box>

      {systems.map((system) => {
        const people = order(bySystem[system].filter((p) => !isMachine(p)));
        const machines = order(bySystem[system].filter(isMachine));
        return (
          <Box key={system} gap="lg">
            <Heading as="h3" size="sm">{systemName(system)}</Heading>
            <Rows
              principals={people}
              editable={editable}
              editing={editing}
              setEditing={setEditing}
              onSaved={onSaved}
            />
            {machines.length > 0 && (
              <Box gap="sm">
                <Box as="span" fontSize="sm" color="secondary">
                  Service accounts ({machines.length}) — no job role to be commensurate with, kept in
                  the population because the practice asks that they be documented.
                </Box>
                <Rows
                  principals={machines}
                  editable={editable}
                  editing={editing}
                  setEditing={setEditing}
                  onSaved={onSaved}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * The population as a grid, so a flagged account can carry its finding and its
 * form across the full width underneath its own row.
 *
 * Not a Table: a table cell is the wrong shape for a sentence and the wrong
 * shape for a form. The finding squeezed into a column wrapped to ten lines and
 * the form had nowhere to go at all.
 */
function Rows({ principals, editable, editing, setEditing, onSaved }) {
  if (principals.length === 0) return null;

  return (
    <Box className="population">
      {['Account', 'Held by', 'Privilege', 'Last seen', ''].map((heading, index) => (
        <Box key={heading || index} className="population__heading">{heading}</Box>
      ))}

      {principals.map((principal) => {
        const cell = `population__cell${principal.flagged ? ' population__cell--flagged' : ''}`;
        const needsDecision = principal.finding && !principal.note;
        // A flag the review raised is answered, never cleared, so there is
        // nothing to offer on those. A flag a person raised can be taken back.
        const canUnflag = principal.note && !principal.finding;
        // The form is simply there on a flagged row rather than behind a
        // toggle. An Edit button that reveals the thing you came to use is a
        // step that exists to be clicked.
        const showDetail = principal.flagged || editing === principal.id;

        return (
          <Fragment key={principal.id}>
            <Box className={cell}>
              <Box direction="row" gap="sm" alignItems="center">
                <Box as="span" fontWeight={principal.flagged ? 'bold' : 'regular'}>
                  {principal.identifier}
                </Box>
                {needsDecision && <Badge variant="soft" color="red">needs a decision</Badge>}
                {principal.flagged && !needsDecision && (
                  <Badge variant="soft" color="warning">flagged</Badge>
                )}
              </Box>
            </Box>
            <Box className={cell}>
              {isMachine(principal) ? (
                <Box as="span" color="tertiary">service account</Box>
              ) : (
                principal.person || <Box as="span" color="tertiary">not recorded</Box>
              )}
            </Box>
            <Box className={cell}>{principal.privilege}</Box>
            <Box className={cell}>
              {principal.activity_known ? (
                principal.last_activity?.slice(0, 10)
              ) : (
                <Box as="span" color="tertiary">not reported</Box>
              )}
            </Box>
            <Box className={`${cell} population__cell--action`}>
              {editable && !principal.flagged && editing !== principal.id && (
                <Button size="sm" variant="secondary" onClick={() => setEditing(principal.id)}>
                  Flag
                </Button>
              )}
              {editable && canUnflag && (
                <Unflag principal={principal} onSaved={onSaved} />
              )}
            </Box>

            {showDetail && (
              <Box className="population__detail" gap="sm">
                {principal.finding && <Box as="p" maxWidth="52rem">{principal.finding}</Box>}
                {editable && principal.needs_identity && (
                  <Identify principal={principal} onSaved={onSaved} />
                )}
                {editable && !principal.needs_identity ? (
                  <Note
                    principal={principal}
                    onSaved={onSaved}
                    onCancel={principal.flagged ? null : () => setEditing(null)}
                  />
                ) : null}
                {!editable && (
                  <>
                    {principal.note && <Box as="p" maxWidth="52rem">{principal.note}</Box>}
                    {principal.remediation_url && (
                      <a href={principal.remediation_url} target="_blank" rel="noreferrer">
                        {principal.remediation_url}
                      </a>
                    )}
                  </>
                )}
              </Box>
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}

/**
 * What each system is called, rather than what its captures are keyed by.
 * "workspace" and "gcp" are artifact-name shorthand; nobody calls them that.
 */
const SYSTEM_NAMES = {
  workspace: 'Google Workspace',
  gcp: 'Google Cloud',
  github: 'GitHub',
};

const systemName = (system) => SYSTEM_NAMES[system] ?? system;

/** A service account, from what the provider called it. */
function isMachine(principal) {
  return (
    principal.identifier.startsWith('serviceAccount:') ||
    principal.identifier.endsWith('.gserviceaccount.com')
  );
}

/**
 * The two ways a review ends that are not a signature.
 *
 * Closing keeps it, with the reason, because "we closed this without signing it"
 * is a fact about the control's operation that an auditor may ask about.
 * Deleting removes it, for one opened by mistake or run as a test — and is
 * refused once anybody has signed, because a record that can be deleted after
 * it is attested to is not a record.
 */
function EndWithoutSigning({ path, onClosed, onDeleted }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(null);
  const [confirming, setConfirming] = useState(false);

  async function run(name, request, after) {
    setBusy(name);
    setFailed(null);
    try {
      await request();
      await after();
    } catch (err) {
      setFailed(err.message);
      setBusy(null);
    }
  }

  return (
    <Box gap="lg" padding="xl" borderWidth="sm" borderColor="subtle" radius="md">
      <Heading as="h2" size="md">End it without signing</Heading>
      {failed && <Alert variant="warning">{failed}</Alert>}

      <Box as="p" color="secondary" fontSize="sm" maxWidth="48rem">
        A review opened by mistake, superseded, or run as a test should not be signed off to get rid
        of it. Close it with a reason and it is kept as a review that was never attested to; delete
        it and it is gone.
      </Box>

      <Box gap="sm">
        <TextInput
          id="close-reason"
          label="Why it is being closed"
          placeholder="superseded by a second run"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Box direction="row">
          <Button
            size="sm"
            variant="secondary"
            isLoading={busy === 'close'}
            isDisabled={!reason.trim() || Boolean(busy)}
            onClick={() => run('close', () => api.post(`${path}/close`, { reason }), onClosed)}
          >
            Close without signing
          </Button>
        </Box>
      </Box>

      <Box direction="row" gap="sm" alignItems="center">
        {!confirming && (
          <Button size="sm" variant="danger" isDisabled={Boolean(busy)} onClick={() => setConfirming(true)}>
            Delete this review
          </Button>
        )}
        {confirming && (
          <>
            <Box as="span" fontSize="sm">Delete it and everything in it?</Box>
            <Button
              size="sm"
              variant="danger"
              isLoading={busy === 'delete'}
              onClick={() => run('delete', () => api.delete(path), onDeleted)}
            >
              Yes, delete
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>Cancel</Button>
          </>
        )}
      </Box>
    </Box>
  );
}
